/*
 * @Description: 地图点位（POI）— E1「列表」数据 hook。
 *   POI 上限 50/团队，一次拉全（不分页）；关键词搜索按名称/地址过滤（真实走后端，mock 本地过滤）。
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { useAppDispatch } from '../../../app/hooks'
import { fetchPoiList } from '../../../actions/locationCockpit'
import { isCockpitMockEnabled } from '../constants'
import { MOCK_POI_LIST } from '../mock/poi.mock'
import { CockpitEnvelope } from '../types/cockpit.types'
import { PoiItem, PoiListData } from '../types/poi.types'

/** POI 上限 50/团队，一次拉全 */
const PAGE_SIZE = 50

export interface UsePoiListResult {
  pois: PoiItem[]
  /** 团队 POI 总数（用于 50 上限判断；关键词为空时即全量） */
  total: number
  loading: boolean
  /** 刷新（搜索变化 / 增删后调用） */
  refresh: () => void
}

/**
 * 拉取地图点位列表（E1 /poi/list）。
 *
 * @param groupId - 当前团队长 id
 * @param keyword - 名称/地址搜索关键词（空 = 全部）
 * @param enabled - 是否启用（管理弹窗打开时才拉，关闭不请求）
 */
export function usePoiList(
  groupId: string,
  keyword: string,
  enabled: boolean
): UsePoiListResult {
  const dispatch = useAppDispatch()
  const isMock = isCockpitMockEnabled()
  const [pois, setPois] = useState<PoiItem[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const aliveRef = useRef(true)

  const refresh = useCallback(() => {
    if (!enabled) return
    if (isMock) {
      const kw = keyword.trim()
      const items = kw
        ? MOCK_POI_LIST.items.filter(
            (p) => p.name.includes(kw) || p.address.includes(kw)
          )
        : MOCK_POI_LIST.items
      setPois(items)
      setTotal(MOCK_POI_LIST.total)
      return
    }
    if (!groupId) return
    setLoading(true)
    dispatch(fetchPoiList({ groupId, keyword, pageNo: 1, pageSize: PAGE_SIZE }))
      .then((res: CockpitEnvelope<PoiListData>) => {
        if (!aliveRef.current) return
        setLoading(false)
        if (res && res.code === 200) {
          setPois(res.data?.items ?? [])
          setTotal(res.data?.total ?? 0)
        }
      })
      .catch(() => {
        if (!aliveRef.current) return
        setLoading(false)
      })
  }, [dispatch, groupId, keyword, enabled, isMock])

  useEffect(() => {
    aliveRef.current = true
    refresh()
    return () => {
      aliveRef.current = false
    }
  }, [refresh])

  return { pois, total, loading, refresh }
}
